/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { FieldArray, Field, Form, Formik } from 'formik';
import { debounce } from "lodash";
import * as yup from 'yup';
import Icon from 'components/Icon';
import {
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCollum,
  InlineLabel,
  DialogIcon,
  ViewText,
  RadioContainer,
  CalendarContainer,
  ButtonContainer,
  ButtonContent,
  LabelEdit
} from './styles';
import { GridColumnTitle, Label } from 'components/Form';
import CheckBox from "components/Form/CheckBox";
import Row from 'components/Grid/Row';
import Col from 'components/Grid/Col';
import ComboBox from 'components/Form/ComboBox3';
import InputField from 'components/Form/InputField';
import { FitinsurAPI, MessageCenter } from 'utils';
import Button from 'components/Button';
import { GlobalLoaderContext, GlobalAuthContext } from 'store/context';
import { RadioButton } from "components/Form/Radio/index";
import {DayPicker} from 'react-day-picker';
import SearchField from 'components/Form/SearchField';
import ModalConfirmDelete from 'components/ModalConfirmDelete';

const ThirdPartyInformation = ({ idPerson, columns, data, hasPermission, onSavePersonRelationship }) => {
  const formikRef = useRef(null);
  const inputRef = useRef(null);
  const fitinsur = new FitinsurAPI();
  const mc = new MessageCenter();
  const loading = useContext(GlobalLoaderContext);
  const authContext = useContext(GlobalAuthContext);
  const [validations, setValidations] = useState({});

  const [sortBy, setSortBy] = useState({ key: null, direction: null, });
  const [addPersonRelationship, setAddPersonRelationship] = useState(false);
  const [idPersonRelationship, setIdPersonRelationship] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [searchList, setSearchList] = useState([])
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const [initial, setInitial] = useState({
    contractExpireDate: '',
    contractStatus: '',
    personTailLastName: '',
    idPersonTail: '',
  });

  const setEditPersonRelationship = (personRelationship) => {
    formikRef.current.values.ndaStatus = personRelationship.relationshipContent.contractExpireDate;
    formikRef.current.values.capacityExposure = personRelationship.relationshipContent.contractStatus;
    formikRef.current.values.personTailLastName = personRelationship.personTailLastName;
    formikRef.current.values.idPersonTail = personRelationship.idPersonTail;

    setAddPersonRelationship(true);
    setIdPersonRelationship(personRelationship.idPersonRelationship);
    setTimeout(() => inputRef.current && inputRef.current.focus(), 100)
  }

  const resetForm = () => {
    formikRef.current.values.contractExpireDate = '';
    formikRef.current.values.contractStatus = '';
    formikRef.current.values.personTailLastName = '';
    formikRef.current.values.idPersonTail = '';
  }

  const saveRelationship = async (body) => {
    try {
      loading.start();
      body.idPersonHead = idPerson;
      body.idPersonRelationship = idPersonRelationship;
      body.idDomain = 449;
      body.relationshipContent = {
        contractExpireDate: body.contractExpireDate,
        contractStatus: body.contractStatus,
      }

      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur.requestSavePersonRelationship(body).then(res => res.json());
      if (response.ret_code !== 200) {
        mc.error(response.ret_msg_details)
        loading.stop();
        return;
      }

      setIdPersonRelationship(null);
      setAddPersonRelationship(false);
      resetForm();
      onSavePersonRelationship && onSavePersonRelationship();
    } catch (err) {
      loading.stop();
      mc.error("An error happened, please try again.");
    };
  }

  const deletePersonRelationship = async (idPersonRelationship) => {
    try {
      loading.start();
      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur.requestDeletePersonRelationship(idPersonRelationship).then(res => res.json());
      setIdPersonRelationship(null);

      if (response.ret_code !== 200) {
        mc.error(response.ret_msg_details)
        loading.stop();
        return;
      }

      onSavePersonRelationship && onSavePersonRelationship();
    } catch (err) {
      setIdPersonRelationship(null);
      loading.stop();
      mc.error("An error happened, please try again.");
    };
  }

  const searchCompanies = async (searchValue) => {
    try {
      fitinsur.setAuthToken(authContext.token);
      fitinsur.requestGetPersonsList(`?fullName=${searchValue}&personType=35`).then((response) => response.json()).then((data) => {
        if (data.ret_code != 200) {
          return;
        }

        data.ret_value = data.ret_value?.map(v => ({ ...v, id: v.idPerson, personName: `${v.lastName}` }))
        setSearchList(data.ret_value);
      });
    } catch (err) {
      console.log("FETCH_FAILURE: ", err);
    }
  }

  const createValidation = () => {
    let validations = yup.object().shape({
      idPersonTail: yup.string().required("* Required field"),
      contractExpireDate: yup.string().required("* Required field"),
      contractStatus: yup.string().required("* Required field"),
    });
    setValidations(validations);
    return validations
  }

  const debouncedChangeHandler = useCallback(
    debounce(searchCompanies, 300)
    , []);

  useEffect(() => {
    createValidation();
  }, []);

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initial}
      enableReinitialize={true}
      onSubmit={(values) => saveRelationship(values)}
      validationSchema={validations}
    >
      {({ setFieldValue, values }) => (
        <>
          <ModalConfirmDelete show={deleteModalOpen} onModalClose={() => { setDeleteModalOpen(false); setIdPersonRelationship(null); }} onConfirm={() => deletePersonRelationship(idPersonRelationship)} />
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((column, index) =>
                  <TableHeader right={column.right} key={index} minWidth={column.minWidth} maxWidth={column.maxWidth}>
                    <GridColumnTitle style={{ cursor: column.value ? 'pointer' : 'not-allowed' }}>
                      {column.label}
                    </GridColumnTitle>
                    {column.value && sortBy.key === column.value && sortBy.direction === "asc" ?
                      <Icon name={"arrowup"} /> :
                      column.value && sortBy.key === column.value && sortBy.direction === "desc" ?
                        <Icon name={"arrowdown"} /> :
                        ''}
                  </TableHeader>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              <FieldArray
                name="management"
                render={() => data.map((item, index) => (
                  <TableRow key={index} >
                    <TableCollum>
                      <InlineLabel>
                        3rd Party
                      </InlineLabel>
                      <LabelEdit fontSize={12}>
                        {item.personTailLastName}
                      </LabelEdit>
                    </TableCollum>

                    <TableCollum>
                      <InlineLabel>
                        Contract Status
                      </InlineLabel>
                      <LabelEdit fontSize={12}>
                        {item.relationshipContent.contractStatus ? 'YES' : "NO"}
                      </LabelEdit>
                    </TableCollum>

                    <TableCollum>
                      <InlineLabel>
                        Contract Expiry Date
                      </InlineLabel>
                      <LabelEdit fontSize={12}>
                        {item.relationshipContent.contractExpireDate}
                      </LabelEdit>
                    </TableCollum>

                    <TableCollum right>
                      <InlineLabel>
                        ACTION
                      </InlineLabel>
                      <Label>
                        <Icon
                          name="edit"
                          style={{ alignSelf: "center", cursor: "pointer" }}
                          onClick={() => setEditPersonRelationship(item)}
                        />
                        <Icon
                          name="trash"
                          style={{ alignSelf: "center", cursor: "pointer" }}
                          onClick={() => {
                            setIdPersonRelationship(item.idPersonRelationship);
                            setDeleteModalOpen(true);
                            setAddPersonRelationship(false);
                            resetForm();
                          }} />
                      </Label>
                    </TableCollum>
                  </TableRow>
                ))
                }
              />
            </TableBody>
          </Table>

          {!addPersonRelationship && <Row>
            <Col lg={6} md={6} sm={6} xs={12}></Col>
            <Col lg={6} md={6} sm={6} xs={12}>
              <ViewText style={{ marginTop: '15px' }}>
                <Icon name="plus" onClick={() => setAddPersonRelationship(true)} style={{ cursor: 'pointer' }} />
              </ViewText>
            </Col>
          </Row>}

          {addPersonRelationship && <Form>
            <div style={{ marginTop: 40, marginBottom: 10 }}>
              <Row>
                <Col lg={6} md={6} sm={6} xs={12}>
                  <Field
                    name="personTailLastName"
                    label="3rd Party"
                    isRequired
                    component={SearchField}
                    disabled={idPersonRelationship}
                    searchList={searchList}
                    value={values.personTailLastName && values.personTailLastName}
                    onChange={debouncedChangeHandler}
                    onFocus={() => {
                      setSearchList([]);
                      searchCompanies("");
                    }}
                    canGenerate={false}
                    onSearchSelect={(selectedValue) => {
                      setFieldValue(`personTailLastName`, selectedValue.lastName);
                      setFieldValue(`idPersonTail`, selectedValue.idPerson);
                      setSearchList([]);
                    }}
                  />
                </Col>
                <Col lg={6} md={6} sm={6} xs={12}>
                  <Label>Contract Active</Label>
                  <RadioContainer>
                    <Field
                      style={{ marginRight: 40 }}
                      label={'Yes'}
                      name={'contractStatus'}
                      value={values.contractStatus}
                      onChange={(e) => { setFieldValue('contractStatus', e) }}
                      option={true}
                      component={RadioButton}
                    />
                    <Field
                      label={'No'}
                      name={'contractStatus'}
                      value={values.contractStatus}
                      onChange={(e) => setFieldValue(`contractStatus`, e,)}
                      option={false}
                      component={RadioButton}
                    />
                  </RadioContainer>
                </Col>
                <Col lg={6} md={6} sm={6} xs={12}>
                  <Field
                    label="Contract Expire Date"
                    name="contractExpireDate"
                    component={InputField}
                    rightIcon='calendar'
                    rightIconClick={() => { setShowCalendar(!showCalendar) }}
                    validation={false}
                    type="text"
                    readOnly
                  />

                  {showCalendar &&
                    <CalendarContainer>
                      <DayPicker style
                        disabledDays={[{ before: new Date() }]}
                        fixedWeeks
                        captionLayout="dropdown-buttons"
                        fromYear={1900}
                        toYear={2100}
                        onDayClick={(date) => {
                          const selectedDate = new Date(date) ? new Date(date) <= new Date() ? new Date().toLocaleDateString() : new Date(date).toLocaleDateString() : null;
                          setFieldValue('contractExpireDate', selectedDate);
                          setShowCalendar(false);
                        }}
                      ></DayPicker>
                    </CalendarContainer>
                  }
                </Col>
              </Row>


              <ButtonContainer>
                <ButtonContainer style={{ justifyContent: 'flex-start', alignItems: 'center', paddingBottom: '10px' }} />
                <ButtonContent>
                  <Button
                    fullWidth
                    style={{ marginLeft: 15 }}
                    third
                    onClick={() => {
                      setIdPersonRelationship(null);
                      setAddPersonRelationship(false);
                      resetForm();
                    }}
                  >
                    CANCEL
                  </Button>
                </ButtonContent>
                <ButtonContent>
                  <Button
                    fullWidth
                    style={{ marginLeft: 15 }}
                    type="submit"
                    primary
                    onClick={() => {
                      createValidation().validate(values).catch((e) => {
                        console.log('validation error', e)
                        mc.error('Please fill in all required fields.')
                      })
                    }}
                    id="btnSubmit">
                    {idPersonRelationship ? "SAVE" : "ADD"}
                  </Button>
                </ButtonContent>
              </ButtonContainer>
            </div>
          </Form>}
        </>
      )}
    </Formik>
  )
}

export default ThirdPartyInformation;